import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiAlertTriangle, FiCheckCircle, FiXCircle } from "react-icons/fi";
import Button from "components/atoms/Button";
import InputField from "components/atoms/InputField";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import FindingsList from "./FindingsList";
import { VersionStatusPill } from "./ui";

export const RELEASE_MODAL_ID = "releaseModal";

// POST /version-rule/{id}/release — PUBLISH (Draft → FINAL & aktif) atau
// ROLLBACK (Versi FINAL yang pernah dirilis menjadi aktif lagi dari snapshot-nya).
const ReleaseModal = ({ version, releaseType = "PUBLISH", onReleased }) => {
  const isPublish = releaseType === "PUBLISH";
  const [validation, setValidation] = useState({ loading: isPublish, data: null, error: null });
  const [changeNote, setChangeNote] = useState("");
  const [submit, setSubmit] = useState({ loading: false, findings: [], errors: [] });

  useEffect(() => {
    if (!version || !isPublish) return;
    standardTreatmentService
      .getValidation(version.id)
      .then((data) => setValidation({ loading: false, data, error: null }))
      .catch((err) => setValidation({ loading: false, data: null, error: errorMessages(err) }));
  }, [version, isPublish]);

  const confirm = () => {
    setSubmit({ loading: true, findings: [], errors: [] });
    standardTreatmentService
      .releaseVersion(version.id, { release_type: releaseType, change_note: changeNote.trim() || null })
      .then((result) => {
        setSubmit({ loading: false, findings: [], errors: [] });
        onReleased(result);
      })
      .catch((err) =>
        setSubmit({
          loading: false,
          // 409 release ditolak (HANDOFF §6.5): data = { message, findings, ... }.
          findings: err?.response?.data?.data?.findings || [],
          errors: [
            ...errorMessages(err),
            ...(typeof err?.response?.data?.data?.message === "string" ? [err.response.data.data.message] : []),
          ],
        })
      );
  };

  const v = validation.data;
  const canConfirm = version && !submit.loading && (!isPublish || v?.valid);

  return (
    <div className="modal fade md-modal" id={RELEASE_MODAL_ID} tabIndex={-1} aria-labelledby={`${RELEASE_MODAL_ID}Label`} aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${RELEASE_MODAL_ID}Label`}>
              {isPublish ? "Rilis Versi" : "Rollback ke Versi"} {version?.version}
            </h5>
            <button type="button" className="close" data-dismiss="modal" aria-label="Tutup">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          {version && (
            <div className="modal-body">
              <dl className="md-detail mb-3">
                <dt>Versi</dt>
                <dd>Versi {version.version}</dd>
                <dt>Status saat ini</dt>
                <dd>
                  <VersionStatusPill status={version.status} />
                </dd>
                <dt>Setelah {isPublish ? "rilis" : "rollback"}</dt>
                <dd>
                  {isPublish
                    ? "Status menjadi FINAL (beku) dan menjadi Versi aktif."
                    : "Menjadi Versi aktif lagi dengan menyalin snapshot rilis aslinya."}
                </dd>
                {isPublish && v && (
                  <>
                    <dt>Ringkasan</dt>
                    <dd>
                      {v.counts.rule_count} rule · {v.counts.condition_count} kondisi · {v.counts.therapy_count} terapi
                    </dd>
                  </>
                )}
              </dl>

              {isPublish && (
                <>
                  {validation.loading && <p className="mps-muted">Memeriksa kelengkapan versi...</p>}
                  {validation.error && (
                    <div className="mps-alert is-error">
                      <FiXCircle size={20} className="mps-alert-icon" />
                      <p className="mps-alert-text">{validation.error.join(", ")}</p>
                    </div>
                  )}
                  {v && (
                    <div className={`mps-alert ${v.valid ? "is-success" : "is-error"}`}>
                      {v.valid ? (
                        <FiCheckCircle size={20} className="mps-alert-icon" />
                      ) : (
                        <FiXCircle size={20} className="mps-alert-icon" />
                      )}
                      <div className="flex-grow-1">
                        <p className="mps-alert-title">
                          {v.valid ? "Validasi lulus" : `Belum valid — ${v.finding_count} findings`}
                        </p>
                        {!v.valid && <FindingsList findings={v.findings} />}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* HANDOFF §9: rilis tidak dapat dibatalkan; tawarkan validasi → simulasi dulu. */}
              <p className="md-warning mb-3">
                {isPublish ? "Rilis" : "Rollback"} tidak dapat dibatalkan — setiap rilis menambah baris
                baru di riwayat. Versi hanya bisa diganti dengan rilis/rollback berikutnya.{" "}
                {isPublish && (
                  <Link to={`/master-pengobatan-standar?version_id=${version.id}&step=5`} data-dismiss="modal">
                    Simulasikan kasus dulu
                  </Link>
                )}
              </p>

              <InputField
                type="textarea"
                label="Catatan rilis (change_note)"
                placeholder={isPublish ? "Contoh: Penyesuaian dosis Primaquine" : "Contoh: Versi 3 ditarik"}
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
              />

              {submit.errors.length > 0 && (
                <div className="mps-alert is-error mt-3" role="alert">
                  <FiAlertTriangle size={20} className="mps-alert-icon" />
                  <div className="flex-grow-1">
                    <ul className="mps-alert-list mt-0 mb-2">
                      {submit.errors.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                    <FindingsList findings={submit.findings} />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="modal-footer">
            <Button type="button" variant="secondary" size="normal" data-dismiss="modal">
              Batal
            </Button>
            <Button type="button" variant="primary" size="normal" onClick={confirm} isDisabled={!canConfirm}>
              {submit.loading ? "Memproses..." : isPublish ? "Rilis sekarang" : "Rollback"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReleaseModal;
